import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { StyleSheet, View } from "react-native";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import ScreenCard from "../ScreenCard";
import axios from "axios";
import { useI18n } from "../../hooks/i18n-hooks";
import TextField from "../../components/TextField";
import VStack from "../../ui_atoms/VStack";
import { useTheme } from "../../hooks/use-theme";
import { Address } from "../../components/address/address";
import AddressField from "../../components/address/AddressField";
import { Document, DocumentType } from "../../__generated__/graphql";
import { StackScreenProps } from "@react-navigation/stack";
import SelectPicture from "../../components/SelectPicture";
import Switch from "../../ui_atoms/Switch";
import { AuthManagerContext } from "../../contexts/AuthManager";
import isEmail from "validator/lib/isEmail";
import Text from "../../components/Text";
import { useIntercom } from "../../hooks/intercom";
import MarkdownView from "../../components/MarkdownView";
import IdeField from "../../ide-public-service/IdeField";
import { ZefixCompany } from "../../zefix/ZefixAPI";
import { SignupProfileType, useSignup } from "../../hooks/use-signup";
import { PickerOptionsMode } from "../../hooks/documents";
import { cantonsMapping } from "../../utils";
import { usePreventRemove } from "@react-navigation/core";
import Gap from "../../ui_atoms/Gap";
import { CREATE_CLIENT_PROFILE } from "../../graphql/core";
import { useMutation } from "@apollo/client";
import { useApolloErrorSnackbar } from "../../hooks/snackbar";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import { LinkSmallTextBold } from "../../components/FormattedText";
import { SignupSigninStackParamList } from "../../navigations/SignupSigninStack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { IdeCompany } from "../../ide-public-service/IdeAPI";

type ZefixFirmData = {
    address: {
        organisation: string;
        careOf: string;
        street: string;
        houseNumber: string;
        addon: string;
        poBox: string;
        town: string;
        swissZipCode: string;
        country: string;
    };
    shabPub: { registryOfficeCanton: keyof typeof cantonsMapping }[];
};

type FormData = {
    companyLogo: Document;
    company: IdeCompany;
    address: Address;
    email: string;
    allowedToHire: boolean;
    terms: boolean;
};

type CheckInfoScreenProps = StackScreenProps<
    SignupSigninStackParamList & RootStackParamList,
    "CheckClientInfo"
>;

const CheckClientInfo = ({ navigation, route }: CheckInfoScreenProps) => {
    const i18n = useI18n();
    const styles = useStyles();
    const { setProfile } = useContext(ProfileSwitcherManagerContext);
    const { appExpoPushToken, refreshUser } = useContext(AuthManagerContext);
    const { signup, loading: signupLoading, error } = useSignup(SignupProfileType.CLIENT);
    const { isNewProfile = false } = route.params || {};
    const [
        initClientProfile,
        {
            loading: initClientProfileLoading,
            error: initClientProfileError,
            reset: initClientProfileReset,
        },
    ] = useMutation(CREATE_CLIENT_PROFILE);

    const [isRefreshingUser, setIsRefreshingUser] = useState(false);

    const loading = signupLoading || initClientProfileLoading || isRefreshingUser;

    useApolloErrorSnackbar(
        initClientProfileError,
        initClientProfileReset,
        i18n.t("screens.auth.check_client_info.error"),
    );

    const {
        handleSubmit,
        control,
        formState: { errors, isValid },
        setValue,
    } = useForm<FormData>({
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: {},
    });

    useEffect(() => {
        if (isNewProfile) {
            navigation.setOptions({
                headerTitle: i18n.t("screens.profile_editor.new_profile"),
            });
        }
    }, [i18n, isNewProfile, navigation]);

    const { presentArticleId, loginUnidentifiedUser } = useIntercom();
    const openMarkdownLink = useCallback(
        (url: string) => {
            void (async () => {
                if (url.startsWith("intercom://")) {
                    const articleId = url.split("intercom://")[1];
                    await loginUnidentifiedUser();
                    await presentArticleId(articleId);
                }
            })();
            return false;
        },
        [loginUnidentifiedUser, presentArticleId],
    );

    const onSubmit = useCallback<SubmitHandler<FormData>>(
        async (data) => {
            const { email, address, companyLogo, company } = data;

            if (isValid) {
                const clientData = {
                    name: company.name,
                    uid: company.uid,
                    address,
                    ...(companyLogo?.documentUrl ? { logoUrl: companyLogo.documentUrl } : {}),
                };
                if (!isNewProfile) {
                    await signup({
                        businessEmail: email,
                        user: { email, expoPushToken: appExpoPushToken },
                        client: clientData,
                    });
                    navigation.navigate("PreIdVerification");
                } else {
                    setIsRefreshingUser(true);
                    try {
                        const { data: initClientProfileData } = await initClientProfile({
                            variables: {
                                clientProfile: {
                                    businessEmail: email,
                                    client: clientData,
                                },
                            },
                        });
                        await refreshUser?.();
                        await setProfile(initClientProfileData?.createClientProfile || null);
                        // This setTimeout() is necessary (at least on web) to correctly navigate on the ClientVerificationPending screen
                        setTimeout(() => {
                            navigation.navigate("ClientVerificationPending");
                        }, 0);
                    } catch (e) {
                        // console.error(ex);
                        // this is handled by updateError in useMutation
                    } finally {
                        setIsRefreshingUser(false);
                    }
                }
            }
        },
        [
            appExpoPushToken,
            initClientProfile,
            isNewProfile,
            isValid,
            navigation,
            refreshUser,
            setProfile,
            signup,
        ],
    );

    // keep the zefix code to allow come back to zefix if needed
    //eslint-disable-next-line
    const getZefixCompanyAddress = useCallback(
        async (company: ZefixCompany) => {
            const { data: zeFixData } = await axios.get<ZefixFirmData>(
                `https://zefix.ch/ZefixREST/api/v1/firm/${company.ehraid}`,
            );

            const { street, houseNumber, town, swissZipCode, country } = zeFixData.address;

            const countryName = country === "CH" ? "Switzerland" : undefined;

            let formatted = street;

            if (houseNumber) {
                formatted += ` ${houseNumber}`;
            }

            formatted += `, ${swissZipCode} ${town}`;

            if (countryName) {
                formatted += `, ${countryName}`;
            }

            const cantonCode = zeFixData.shabPub?.[0]?.registryOfficeCanton;
            // in some cases we do not receive this information so we let user enter the address manually
            if (!cantonCode) {
                return;
            }
            setValue("address", {
                locality: {
                    name: town,
                    postalCode: swissZipCode,
                    state: {
                        code: cantonCode,
                        name: cantonsMapping[cantonCode],
                        country: { code: country, name: countryName || "" },
                    },
                },
                route: street,
                streetNumber: houseNumber,
                formatted,
            });
        },
        [setValue],
    );

    const getIdeCompanyAddress = useCallback(
        (company: IdeCompany) => {
            if (company?.address) {
                const { street, houseNumber, postalCode, city, cantonAbbreviation, country } =
                    company.address;

                const countryName = country === "CH" ? "Switzerland" : undefined;

                let formatted = street;

                if (houseNumber) {
                    formatted += ` ${houseNumber}`;
                }

                formatted += `, ${postalCode} ${city}`;

                if (countryName) {
                    formatted += `, ${countryName}`;
                }

                const cantonCode: keyof typeof cantonsMapping =
                    cantonAbbreviation as keyof typeof cantonsMapping;
                // in some cases we do not receive this information so we let user enter the address manually
                if (!cantonCode) {
                    return;
                }
                setValue("address", {
                    locality: {
                        name: city,
                        postalCode: postalCode,
                        state: {
                            code: cantonCode,
                            name: cantonsMapping[cantonCode],
                            country: { code: country, name: countryName || "" },
                        },
                    },
                    route: street,
                    streetNumber: houseNumber,
                    formatted,
                });
            }
        },
        [setValue],
    );

    usePreventRemove(loading, () => null);

    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.t("submit"),
                    isValid: isValid && !loading,
                    validating: loading,
                    onPress: handleSubmit(onSubmit),
                },
            ]}
            headband={
                error
                    ? {
                          text: i18n.t("screens.auth.check_client_info.error"),
                          mode: "error",
                      }
                    : undefined
            }
        >
            <VStack gap={1} h="2/3">
                <>
                    <Text style={styles.introText}>
                        {i18n.t(`screens.auth.check_client_info.intro`)}
                    </Text>
                    <LinkSmallTextBold
                        onPress={() => {
                            void presentArticleId("10938847");
                        }}
                    >
                        {i18n.t("know_more")}
                    </LinkSmallTextBold>
                </>
                <Controller
                    name="companyLogo"
                    control={control}
                    rules={{
                        required: false,
                    }}
                    render={({ field: { onChange, value } }) => {
                        return (
                            <SelectPicture
                                label={i18n.t("screens.auth.check_client_info.import_company_logo")}
                                value={value}
                                onSelect={onChange}
                                documentType={DocumentType.CompanyPicture}
                                error={!!errors.companyLogo?.message}
                                webUploadFn="companyLogo"
                                modes={[
                                    PickerOptionsMode.Camera,
                                    PickerOptionsMode.Library,
                                    PickerOptionsMode.Web,
                                ]}
                                withPrefix
                                disabled={loading}
                                enableCropZoom
                            />
                        );
                    }}
                />
                <Controller
                    name="company"
                    control={control}
                    rules={{
                        required: true,
                    }}
                    render={({ field: { onChange, onBlur, value } }) => (
                        <IdeField
                            label={i18n.t("screens.auth.check_client_info.company_name_label")}
                            value={value}
                            onSelect={(company) => {
                                onChange(company);
                                if (company?.address) {
                                    getIdeCompanyAddress(company);
                                }
                            }}
                            onBlur={onBlur}
                            error={!!errors.company}
                            disabled={loading}
                        />
                    )}
                />
                <Controller
                    name="address"
                    control={control}
                    rules={{
                        required: true,
                    }}
                    render={({ field: { onChange, value } }) => (
                        <AddressField
                            label={i18n.t("screens.auth.check_client_info.company_address_label")}
                            value={value}
                            onSelect={onChange}
                            error={!!errors.address}
                            allowedCountries={["ch"]}
                            disabled={loading}
                        />
                    )}
                />
                <Controller
                    name="email"
                    control={control}
                    rules={{
                        required: true,
                        validate: (value) =>
                            !isEmail(value) ? i18n.t("errors.field_name.email") : undefined,
                    }}
                    render={({ field: { onChange, onBlur, value } }) => (
                        <TextField
                            label={i18n.t("screens.auth.check_client_info.email_label")}
                            value={value}
                            onChangeText={onChange}
                            onBlur={onBlur}
                            keyboardType="email-address"
                            textContentType="emailAddress"
                            autoComplete="email"
                            autoCorrect={false}
                            error={errors.email?.message}
                            disabled={loading}
                        />
                    )}
                />

                <Controller
                    name="terms"
                    control={control}
                    rules={{
                        required: true,
                    }}
                    render={({ field: { onChange, value } }) => (
                        <View style={styles.termsContainer}>
                            <Switch
                                testID="switch-accept-terms"
                                value={value}
                                onChange={onChange}
                                disabled={loading}
                            />
                            <Gap gap="1/2" />
                            <MarkdownView
                                style={styles.termsMarkdown}
                                markdown={i18n.t(
                                    "screens.auth.check_client_info.accept_terms_of_use",
                                )}
                                onLinkPress={openMarkdownLink}
                            />
                        </View>
                    )}
                />
            </VStack>
        </ScreenCard>
    );
};

const useStyles = () => {
    const { fonts, gridSize } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                introText: {
                    ...fonts.bodyMedium,
                    marginTop: gridSize,
                },
                termsContainer: {
                    flexDirection: "row",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                },
                termsText: {
                    ...fonts.bodyMedium,
                    flexShrink: 1,
                },
                termsMarkdown: {
                    /* compensate paragraph margin of markdown */
                    marginTop: -gridSize / 4,
                    flexShrink: 1,
                },
            }),
        [fonts.bodyMedium, gridSize],
    );
};

export default CheckClientInfo;
